import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { appConfig } from './../../app.config';
import { RepoAuthService } from './../../data/repositories/repo-auth.service';
import { AppSettingsService } from './app-settings.service';
import { BxLogger } from './bx-logger.service';
import { Router } from '@angular/router';

interface IHTMLElement extends HTMLElement {
    ononline: Function;
    onoffline: Function;
}

type OfflineReason = 'windowEventOffline' | 'bodyEventOffline' | 'pingFail';

@Injectable({ providedIn: 'root' })
export class ConnectivityService {
    apiCheck = undefined;
    running = false;
    config: any = undefined;

    constructor(
        private http: HttpClient,
        public auth: RepoAuthService,
        private appSettings: AppSettingsService,
        private logger: BxLogger,
        private router: Router
    ) {
        this.config = appConfig().config;
    }

    start() {
        this.appSettings.online = navigator.onLine;

        if (window.addEventListener) {
            window.addEventListener(
                'online',
                () => {
                    this.setStatus(true);
                },
                true
            );
            window.addEventListener(
                'offline',
                () => {
                    this.setStatus(false, 'windowEventOffline');
                },
                true
            );
        } else {
            (<IHTMLElement>document.body).ononline = () => {
                this.setStatus(true);
            };
            (<IHTMLElement>document.body).onoffline = () => {
                this.setStatus(false, 'bodyEventOffline');
            };
        }

        const everyMinute = 1000 * 10;

        this.running = true;

        // @ts-ignore TS2322
        this.apiCheck = setInterval(() => this.checkApiConnectivity(), everyMinute);
    }

    ngOnDestroy() {
        clearInterval(this.apiCheck);
    }

    stop() {
        this.running = false;

        if (this.apiCheck) clearInterval(this.apiCheck);
    }

    checkApiConnectivity() {
        this.auth.isAuthenticated().then((authenticated) => {
            if (authenticated && this.running && navigator.onLine) {
                // use running incase it's been cancelled but not in time to stop this call

                const now = new Date();
                const headers = new HttpHeaders().set('X-HIDEOVERLAY', '1');
                this.http
                    // @ts-ignore TS2532
                    .get(`${this.config.remoteServiceBaseURL}ping/${now.valueOf()}`, { headers: headers, observe: 'response' })
                    .pipe(
                        map((response) => {
                            if (response?.ok) {
                                this.setStatus(true);
                            }
                        }),
                        catchError((error: any) => {
                            if (error && error.status && (error.status === 401 || error.status === 403)) {
                                this.setStatus(true); // We are online, just not authenticated
                                // close all open Modals before redirection
                                // @ts-ignore TS7015
                                if (window['activeModal'] && window['activeModal'].length > 0) {
                                    // @ts-ignore TS7015
                                    window['activeModal'].forEach((modal) => modal.dismiss());
                                }
                                this.router.navigate(['/login']);
                            } else if (error.error === 'invalid_grant') {
                                // We are online, just not authenticated and
                                // we will let the auth0 refresh provide our authentication which is done via a modal and timer in run.ts
                                this.setStatus(true);
                            } else if (error && window.location.pathname === '/login') {
                                // do not display the connectivity error dialog as we want to original error message stays on the login error page
                                this.setStatus(true);
                            } else {
                                this.setStatus(false, 'pingFail');
                            }
                            return EMPTY;
                        })
                    )
                    .subscribe();
            }
        });
    }

    setStatus(val: boolean, reason?: OfflineReason) {
        const lastVal = this.appSettings.online;
        if (lastVal !== val) {
            this.appSettings.online = val;
            if (!val) {
                this.showOffline();
                if (reason) {
                    this.logger.metric({ name: `Offline - ${reason}`, average: 1 });
                }
            } else this.hideOffline();
        }
    }

    showOffline() {
        /* TODO - To be considered for refactoring after Angular upgrade */
        const appName = this.appSettings.app.name;

        Swal.fire({
            title: 'Check your internet connection<span class="bx-dialog-pulsate">&nbsp;...</span>',
            text: `Either your internet is down, or there are problems connecting to ${appName}. 
            We'll return you to ${appName} when its back up.`,
            icon: 'warning',
            customClass: { popup: 'bx-dialog bx-dialog-warning' },
            allowOutsideClick: false,
            showConfirmButton: false,
            reverseButtons: true,
            showCancelButton: false,
            animation: false,
        });
    }

    hideOffline() {
        Swal.close();
    }
}
